import { action, runInAction, makeObservable, observable } from "mobx";
import { projectsDataStore } from "Stores/ProjectsStore";
import { ListStore } from "Stores/RESTStore/ListStore";
import { API_URL } from "General/APIEndpoints";
import { DataStore } from "Stores/RESTStore/DataStore";
import { EditStore } from "Stores/RESTStore/EditStore";
import { API } from "General/API";
import { CoreStore, handleStoreState } from "Stores/RESTStore/CoreStore";

type API_PROBISCONTRACTTITLE_STATUS =
  | "RESERVED"
  | "BUDGETED"
  | "EXPECTED"
  | "DECLARED"
  | "RECEIVED"
  | "VERIFIED"
  | "COMMISSIONED"
  | "REJECTED"
  | "CANCELLED";
type API_PROBISCONTRACTTITLE_TYPE = "MAIN_CONTRACT" | "VARIATION" | "RESERVE";

export default interface API_ProbisContractTitle {
  adjustmentNet?: number;
  advancedInvoicing?: boolean;
  code?: string;
  commissionDate?: string;
  description?: string;
  discountFactor?: number;
  initialNet?: number;
  status?: API_PROBISCONTRACTTITLE_STATUS;
  type?: API_PROBISCONTRACTTITLE_TYPE;
  userDefinedColumnCode?: string;
  userDefinedColumnValue?: string;
  vat?: number;
  verifiedNet?: number;
}

const tagOptions: { [key in API_STEPINFOREST_STATE]: string } = {
  ABORTED: "Aborted",
  ERROR: "Error",
  FINISHED: "Done",
  IN_PROGRESS: "In Progress",
  STARTED: "Started ", // the space is needed, cause of needed german "Started":"gestartet am" and "Started":"Startet"
  WAITING: "Waiting",
  UNDEFINED: "Undefined",
};

export function getWorkflowStatusStringKey(status: API_STEPINFOREST_STATE) {
  return tagOptions[status] || "Unknown";
}

class WorkflowStepList extends ListStore<API_WorklistEntryRest> {
  url() {
    return API_URL.WORKFLOWS.getWorkflowTasks(projectsDataStore.projectId);
  }

  getWorkflowsByType(type: API_WORKLISTENTRYREST_WORKLISTTYPE) {
    return this.list.slice().filter((wf) => wf.worklistType === type);
  }
}

export const workflowStepList = new WorkflowStepList();

class WorkflowActionEdit extends EditStore<API_ActionInfoRest> {
  identifier = "worklistEntryId";
  sourceDataFields = [];

  url(id: number) {
    return API_URL.WORKFLOWS.changeWorkflowStep(projectsDataStore.projectId, id);
  }
}

class WorkflowActionEditGUI extends WorkflowActionEdit {
  constructor(dataStore, listStore) {
    super(dataStore, listStore);

    makeObservable(this, {
      setFieldsValue: action,
    });
  }

  setFieldsValue(values: object) {
    this.data.guiRest.guiElements.forEach((val) => {
      if (values.hasOwnProperty(val.name)) {
        // check if form object includes desired field event if it's empty
        val.value = values[val.name];
      }
      return val;
    });
  }
}

class WorkflowActionStore extends DataStore<API_ActionInfoRest> {
  identifier = "worklistEntryId";
  listStore = workflowStepList;

  constructor() {
    super();

    makeObservable(this, {
      updateFiles: action,
      updateAdhoc: action,
      setWorkflowVariables: action,
      createTask: action,
      cancelTask: action,
      removeFile: action.bound,
      updateTableData: action.bound,
    });
  }

  url(id: number): string {
    return API_URL.WORKFLOWS.getWorkflowTaskById(projectsDataStore.projectId, id);
  }

  updateFiles(newItems: API_FileInfo[]) {
    if (this.data.workflowInfo) {
      this.data.workflowInfo.files = [...this.data.workflowInfo.files, ...newItems];
    } else {
      this.data.workflowInfo = { files: newItems };
    }
  }

  updateTableData(newData) {
    this.data.guiRest.guiElements.find((item) => item.type === "Table").tableForWorkflows.titleData = newData;
  }

  updateAdhoc(newItems: API_AdHocTaskRest[]) {
    if (this.data.adHoc) {
      this.data.adHoc.tasks = [...this.data.adHoc.tasks, ...newItems];
    } else {
      this.data.adHoc.tasks = newItems;
    }
  }

  setWorkflowVariables(workflowVariables: API_ActionInfoRest["workflowInfo"]["workflowVariables"]) {
    console.log({ ...this.data });
    this.data.workflowInfo.workflowVariables = {
      ...this.data.workflowInfo.workflowVariables,
      ...workflowVariables,
    };
  }

  async createTask(step: string) {
    const body: API_StringData = { stringValue: step };
    try {
      const response: API_NewAdHocTask = await API.newData(
        API_URL.WORKFLOWS.createAdHocTask(projectsDataStore.projectId, this.data.worklistEntryId),
        body
      );
      return response;
    } catch (error) {
      throw error;
    }
  }

  async cancelTask(adHocActionId: number | string) {
    try {
      await API.deleteData(
        API_URL.WORKFLOWS.deleteAdHocTask(projectsDataStore.projectId, this.data.worklistEntryId, adHocActionId)
      );
    } catch (error) {
      throw error;
    }
  }

  @handleStoreState()
  async removeFile(fileId: number) {
    try {
      await API.deleteData(
        API_URL.WORKFLOWS.removeFileFromWorkflow(projectsDataStore.projectId, this.data.worklistEntryId, fileId)
      );
      runInAction(() => {
        this.data.workflowInfo.files = this.data.workflowInfo.files.filter((val) => val.id !== fileId);
      });
    } catch (error) {
      throw error;
    }
  }

  removeAdhoc(taskId: number | string) {
    this.data.adHoc.tasks = this.data.adHoc.tasks.filter((val) => +val.id !== +taskId);
  }

  @handleStoreState()
  async fetch(id: number) {
    try {
      const response = await API.getData(this.url(id), { cache: true });
      runInAction(() => {
        this.data = response;
        if (this.listStore) {
          this.listStore.setItem(this.data);
        }
        if (this.editStore) {
          this.editStore.setData(this.data);
        }
      });
    } catch (error) {
      throw error;
    }
  }

  async attachDocuments(workflowId: number, fileIds: number[]) {
    try {
      const formBody: API_WorkflowAttachments = { fileIds };
      await API.newData(API_URL.WORKFLOWS.attachWorkflowDocs(projectsDataStore.projectId, workflowId), formBody);
    } catch (error) {
      throw error;
    }
  }
}

export interface FlowChartStep {
  name?: string;
  title?: string;
  stepId?: number | string;
  actionType?: API_WORKFLOWFLOWCHARTOBJECT_TYPE;
  action?: string;
  nextSteps?: { id: number | string; action: string }[];
  lastStep?: boolean;
  currentStep?: boolean;
  stepType?: "SYSTEM" | "HUMANTASK";
  xy?: number[];
  isAlone?: boolean; // is only one child step (is used more often for the end step)
}
export interface FlowChartResponse {
  firstStepId: number;
  steps: Record<number, FlowChartStep>;
  grid?: number[];
  wfDescription?: string;
  wfName?: string;
}
class WorkflowChartStore extends CoreStore {
  flowchart: FlowChartResponse = null;

  constructor() {
    super();

    makeObservable(this, {
      flowchart: observable,
      fetch: action.bound,
    });
  }

  url(wfId: number) {
    return API_URL.WORKFLOWS.getWorkflowReactDiagram(projectsDataStore.projectId, wfId);
  }

  clear() {
    this.flowchart = null;
  }

  @handleStoreState()
  async fetch(wfId: number, withFilter = true, prId?: number | string) {
    try {
      const params = withFilter ? { filter: "HUMAN_AND_GW" } : {};
      // if prId than this is admin call
      const url = prId ? API_URL.ADMIN_WORKFLOWS.getAdminWorkflowReactDiagram(prId, wfId) : this.url(wfId);
      const response = await API.getData<FlowChartResponse>(url, params);
      runInAction(() => {
        if (response) {
          this.flowchart = response;
        }
      });
    } catch (error) {
      throw error;
    }
  }

  @handleStoreState()
  async fetchMock(prId: string | number, WFXml: string) {
    try {
      const url = API_URL.ADMIN_WORKFLOWS.getWorkflowReactDiagramFromMockup(prId);
      const response = await API.newData<FlowChartResponse>(url, { WFXml });
      runInAction(() => {
        if (response) {
          this.flowchart = response;
        }
      });
    } catch (error) {
      throw error;
    }
  }
}

export const workflowChartStore = new WorkflowChartStore();

const workflowActionStore = new WorkflowActionStore();
export { workflowActionStore };

const workflowActionEdit = new WorkflowActionEditGUI(workflowActionStore, workflowStepList);
export { workflowActionEdit };
